import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as productActions from "../store/action/product";
import { throttle } from "lodash";

export const useProductFetch = (
  categoryId = "",
  subCategoryId = "",
  childCategoryId = "",
  city = "",
) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { productPagination } = useSelector((state) => state.product);

  // Store previous values and track initial load
  const prevFiltersRef = useRef({
    categoryId,
    subCategoryId,
    childCategoryId,
    city,
  });
  const isInitialLoad = useRef(true);

  const productFetch = useCallback(
    async (newPage = 1, force = false) => {
      if (
        (isLoading && !force) ||
        (!categoryId && !subCategoryId && !childCategoryId)
      )
        return;

      setIsLoading(true);
      try {
        await dispatch(
          productActions.getProducts(
            categoryId,
            subCategoryId,
            childCategoryId,
            city,
            newPage,
          ),
        );
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [categoryId, subCategoryId, childCategoryId, city, dispatch, isLoading],
  );

  // Update pagination info
  useEffect(() => {
    if (productPagination) {
      setHasMore(page < productPagination.totalPages);
    }
  }, [productPagination, page]);

  // Handle filter changes and initial load
  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.categoryId !== categoryId ||
      prevFiltersRef.current.subCategoryId !== subCategoryId ||
      prevFiltersRef.current.childCategoryId !== childCategoryId ||
      prevFiltersRef.current.city !== city;

    // Skip if it's not initial load and no filters changed
    if (!isInitialLoad.current && !filtersChanged) return;

    // Reset pagination only if category/subcategory changed
    const shouldReset =
      prevFiltersRef.current.categoryId !== categoryId ||
      prevFiltersRef.current.subCategoryId !== subCategoryId ||
      prevFiltersRef.current.childCategoryId !== childCategoryId;

    if (shouldReset) {
      setPage(1);
      setHasMore(true);
    }

    productFetch(shouldReset ? 1 : page, true);
    prevFiltersRef.current = {
      categoryId,
      subCategoryId,
      childCategoryId,
      city,
    };
    isInitialLoad.current = false;
  }, [categoryId, subCategoryId, childCategoryId, city, productFetch, page]);

  // Scroll handler with improved throttling
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (isLoading || !hasMore) return;

      const scrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const pageHeight = document.documentElement.offsetHeight;
      const threshold = 500;

      if (pageHeight - scrollPosition < threshold) {
        const nextPage = page + 1;
        setPage(nextPage);
        productFetch(nextPage);
      }
    }, 500); // Increased throttle time

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [isLoading, hasMore, page, productFetch]);

  return { isLoading, hasMore };
};
